import { axiosInstance } from "@/shared/lib/axiosInstance";
import { getThemeClass, getThemeComponentGroup } from "@/shared/lib/themeComponentClasses";
import { filterByItemLabels, setAddOns } from "@/shared/redux/reducers/commonSlice";
import { usePosterReducers } from "@/shared/redux/store/useGetData";
import { getAuthSessionIdentifiers } from "@/shared/utils/authSession";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Checkbox } from "./Checkbox";

type AddonsCommonProps = {
  apiEndpoint: string;
  updateSessionEndpoint: string;
  LOB_ID: number;
  compact?: boolean;
  disabled?: boolean;
};

export const AddonsCommon: React.FC<AddonsCommonProps> = ({
  apiEndpoint,
  updateSessionEndpoint,
  LOB_ID,
  compact = false,
  disabled = false,
}) => {
  const addOnsClasses = getThemeComponentGroup("addOns");
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [checked, setChecked] = useState<string[]>([]);

  const dispatch = useDispatch();
  const {
    commonSlice: { addOns = [], selectedAddOns = [] },
    auth: { session: authSession },
  } = usePosterReducers();

  const { clientId, sessionId } = getAuthSessionIdentifiers(authSession);

  useEffect(() => {
    setChecked(selectedAddOns);
  }, [selectedAddOns]);

  useEffect(() => {
    let isMounted = true;

    const loadAddOns = async () => {
      setIsLoading(true);

      try {
        const response = await axiosInstance.get(apiEndpoint);

        if (isMounted) {
          dispatch(setAddOns(response.data));
        }
      } catch (error) {
        console.error("Failed to load add-ons:", error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadAddOns();

    return () => {
      isMounted = false;
    };
  }, [apiEndpoint, dispatch]);

  const filterAddons = addOns?.find?.(
    (item: any) => item.coverage_group_display_name === "Add-Ons"
  ) ||
    addOns?.[0] || { coverages: [] };

  const postAddOns = async (data: string[]) => {
    if (!sessionId || !clientId) {
      console.warn("Skipping add-on session update due to missing session/client IDs", {
        sessionId,
        clientId,
      });
      return;
    }

    const payload = {
      step: "quote_request",
      payload: {
        selected_addOns: data,
      },
      session_id: Number(sessionId),
      client_id: Number(clientId),
      lob_id: LOB_ID,
    };

    try {
      setIsUpdating(true);

      const response = await axiosInstance.post(updateSessionEndpoint, payload);

      //console.log("Session update response:", response.data);
    } catch (error) {
      console.error("Failed to update session:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const toggleCheck = async (key: string) => {
    if (disabled) {
      return;
    }

    const normalizedKey = String(key || "").trim();
    if (!normalizedKey) return;

    const updatedChecked = checked.includes(normalizedKey)
      ? checked.filter((item: string) => item !== normalizedKey)
      : [...checked, normalizedKey];

    setChecked(updatedChecked);
    dispatch(filterByItemLabels(updatedChecked));
    await postAddOns(updatedChecked);
  };

  const clearAll = async () => {
    if (disabled) {
      return;
    }

    setChecked([]);
    dispatch(filterByItemLabels([]));
    await postAddOns([]);
  };

  return (
    <div
      className={`w-full border shadow-sm ${getThemeClass(
        addOnsClasses.cardBorderColor,
        "border-theme-border"
      )} ${getThemeClass(addOnsClasses.cardBgColor, "bg-theme-surface")} ${
        compact ? "rounded-lg p-3" : "mr-auto max-w-sm rounded-2xl p-6"
      }`}
    >
      {/* Header */}
      <div className={`flex items-center justify-between ${compact ? "mb-2" : "mb-4"}`}>
        <h2
          className={`font-semibold ${getThemeClass(
            addOnsClasses.titleColor,
            "text-theme-text"
          )} ${compact ? "text-sm" : "text-lg"}`}
        >
          Select Add-ons
        </h2>

        <button
          type="button"
          onClick={clearAll}
          disabled={isUpdating || disabled}
          className={`cursor-pointer font-semibold ${getThemeClass(
            addOnsClasses.actionColor,
            "text-theme-text-muted hover:text-theme-success-text-strong"
          )} ${compact ? "text-xs" : "text-sm"}`}
        >
          Clear all
        </button>
      </div>

      {/* List */}
      <div className={compact ? "space-y-2" : "space-y-4"}>
        {filterAddons?.coverages && filterAddons.coverages.length > 0 ? (
          filterAddons.coverages.map((item: any, index: number) => (
            <label
              key={item.coverage_type_id || index}
              className={`flex cursor-pointer items-center justify-between ${compact ? "text-xs" : "text-sm"}`}
            >
              <span
                className={`font-semibold ${getThemeClass(
                  addOnsClasses.textColor,
                  "text-theme-text-soft"
                )} ${compact ? "text-xs" : "text-[13px]"}`}
              >
                {item.coverage_display_name}
              </span>

              <Checkbox
                color="green"
                checked={checked.includes(item.coverage_display_name)}
                disabled={disabled}
                onChange={() => toggleCheck(item.coverage_display_name)}
                className={`h-5 w-5 ${getThemeClass(
                  addOnsClasses.checkboxColor,
                  "border-theme-border-strong focus:ring-green-600"
                )}`}
              />
            </label>
          ))
        ) : isLoading ? (
          <p
            className={`text-sm ${getThemeClass(addOnsClasses.textColor, "text-theme-text-soft")}`}
          >
            Loading add-ons...
          </p>
        ) : (
          <p
            className={`text-sm ${getThemeClass(addOnsClasses.textColor, "text-theme-text-soft")}`}
          >
            No add-ons available
          </p>
        )}
      </div>
    </div>
  );
};
